import type {
  CalendarDay,
  CalendarEvent,
  Meeting,
} from "@/api/calendar";
import {
  format as dateFnsFormat,
  eachDayOfInterval,
  endOfMonth,
  getDay,
  isSameMonth,
  startOfMonth,
} from "date-fns";
import { ar } from "date-fns/locale";
import { gen_color } from "./colors";

export function toUTCISOString(date: Date): string {
  return date.toISOString();
}

export function formatUTCToLocal(
  date: Date,
  formatStr = "HH:mm a",
  options = {}
): string {
  return dateFnsFormat(date, formatStr, { locale: ar, ...options });
}

export function generateMonthCalendar(
  currentDate: Date,
  meetings: Meeting[]
): CalendarDay[] {
  // Generate a range of dates for the month view
  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);

  const allDaysInMonth = eachDayOfInterval({
    start: firstDayOfMonth,
    end: lastDayOfMonth,
  });

  // Adjust for the week start (Monday is 1 in JavaScript/date-fns)
  // Convert from 0-6 (Sun-Sat) to 1-7 (Mon-Sun) and then to 0-6 where 0 is Monday
  const firstDayOfWeek = (getDay(firstDayOfMonth) + 6) % 7;

  // Add days from previous month to start of grid
  const daysFromPrevMonth = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    daysFromPrevMonth.push({
      day: null,
      date: null,
      events: [],
      hasEvent: false,
    });
  }

  // Create calendar days for the current month
  const daysWithEvents = allDaysInMonth.map((date) => {
    // Find events for this day
    const dayEvents = meetings.filter((meeting) => {
      const eventDate = new Date(meeting.start_time * 1000);
      return (
        isSameMonth(eventDate, date) && eventDate.getDate() === date.getDate()
      );
    });

    // Convert meetings to calendar events with the same color
    const events: CalendarEvent[] = dayEvents.map((meeting) => {
      const startDate = new Date(meeting.start_time * 1000);
      const endDate = new Date(meeting.end_time ? meeting.end_time * 1000 : startDate);
      const classes = gen_color(meeting.assigned_to.name)

      return {
        id: meeting.id,
        title: meeting.desc || `Case: ${meeting.case}`,
        startDate,
        endDate,
        color: {
          backgroundColor: classes.backgroundColor,
          borderColor: classes.borderColor
        },
        text_classes: classes.textColorClass,
        originalData: meeting,
      };
    });

    return {
      day: date.getDate(),
      date,
      events,
      hasEvent: events.length > 0,
    };
  });

  // Combine all days
  return [...daysFromPrevMonth, ...daysWithEvents];
}
